import React, { useState, useEffect } from 'react';
import {
  Code,
  Palette,
  Server,
  ShieldCheck,
  Rocket,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  Cpu,
  Globe,
  Layers,
  Terminal,
  Zap,
  Layout,
  MessageSquare
} from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const WebDevelopmentProcess = () => {
  const [activeStage, setActiveStage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const stages = [
    {
      number: '01',
      code: 'TECHNICAL_SCOPING',
      icon: Code,
      title: 'Technical Scoping & Cloud Architecture',
      badge: 'Sprint 0 Architecture',
      timeline: 'Sprint 0',
      description: 'We begin with exhaustive technical discovery, defining database schemas, REST/GraphQL API specifications, and selecting high-throughput cloud stacks to ensure zero technical debt.',
      image: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8?w=800&q=80',
      color: 'from-orange-500 to-amber-600',
      techTags: ['System Architecture', 'DB Schemas', 'GraphQL Spec', 'Cloud Blueprint'],
      deliverables: ['System Architecture & DB Schema', 'REST/GraphQL API Specification', 'Sprint Backlog & Estimation'],
      primaryBtnText: 'Explore Web Architecture Specs',
      primaryBtnLink: '/enterprise-solutions',
      secondaryBtnText: 'Talk to Scoping Engineers',
      secondaryBtnLink: '/contact'
    },
    {
      number: '02',
      code: 'UIUX_DESIGN_TOKENS',
      icon: Palette,
      title: 'Wireframing, UI/UX & Interactive Design System',
      badge: 'Figma Token Library',
      timeline: 'Sprint 1',
      description: 'Our UI/UX team designs pixel-perfect wireframes and interactive Figma prototypes, defining accessibility standards (WCAG 2.1) and custom design token systems for seamless responsiveness.',
      image: 'https://images.unsplash.com/photo-1542744094-3a3172720177?w=800&q=80',
      color: 'from-cyan-500 to-blue-600',
      techTags: ['Figma Design System', 'WCAG 2.1', 'Mobile-First', 'Design Tokens'],
      deliverables: ['Figma Interactive Prototypes', 'Design System & Component Kit', 'WCAG 2.1 Accessibility Clearance'],
      primaryBtnText: 'Explore UI/UX Design Solutions',
      primaryBtnLink: '/ui-ux-design',
      secondaryBtnText: 'Talk to UI/UX Architects',
      secondaryBtnLink: '/contact'
    },
    {
      number: '03',
      code: 'FRONTEND_BACKEND_SYNC',
      icon: Server,
      title: 'Frontend & API Microservices Integration',
      badge: 'React & Next.js Sprints',
      timeline: 'Sprint 2-4',
      description: 'Senior full-stack developers build clean, modular React and Next.js applications, integrating secure authentication, payment gateways, and resilient backend microservices.',
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=800&q=80',
      color: 'from-blue-500 to-indigo-600',
      techTags: ['Next.js 14', 'React 18', 'TypeScript', 'Node.js Microservices'],
      deliverables: ['Clean Modular Codebase', 'GraphQL & REST API Integration', 'Staging Demo Server Build'],
      primaryBtnText: 'Explore Web Engineering Specs',
      primaryBtnLink: '/web-development',
      secondaryBtnText: 'Talk to Lead Developers',
      secondaryBtnLink: '/contact'
    },
    {
      number: '04',
      code: 'SPEED_VITALS_TUNING',
      icon: Zap,
      title: 'Core Web Vitals & Sub-Second Speed Tuning',
      badge: 'Edge SSR Caching',
      timeline: 'Sprint 5',
      description: 'We optimize critical rendering paths using server-side rendering (SSR), image compression, script deferred loading, and Vercel Edge CDN routing to guarantee sub-second page performance.',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      color: 'from-amber-500 to-orange-600',
      techTags: ['Google PageSpeed 98+', 'Core Web Vitals', 'Vercel Edge', 'SSR/SSG'],
      deliverables: ['Google PageSpeed 98+ Audit', 'Core Web Vitals Clearance', 'CDN Edge Caching Configuration'],
      primaryBtnText: 'Check Speed Performance Standards',
      primaryBtnLink: '/contact',
      secondaryBtnText: 'View Speed Case Studies',
      secondaryBtnLink: '/portfolio'
    },
    {
      number: '05',
      code: 'CYPRESS_SECURITY_QA',
      icon: ShieldCheck,
      title: 'Automated Cypress QA & OWASP Penetration Audit',
      badge: 'Zero-Vulnerability QA',
      timeline: 'Sprint 6',
      description: 'Our QA specialists execute automated Cypress end-to-end tests, load tests under simulated peak traffic, and penetration security scans to ensure zero vulnerability before launch.',
      image: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80',
      color: 'from-purple-500 to-pink-600',
      techTags: ['Cypress E2E', 'OWASP Audit', 'Penetration Testing', 'SSL Hardening'],
      deliverables: ['Cypress Automated E2E Test Suite', 'OWASP Penetration Audit Clearance', 'Cross-Browser QA Pass Report'],
      primaryBtnText: 'Explore QA & Security Testing',
      primaryBtnLink: '/qa-testing',
      secondaryBtnText: 'Talk to Security Engineers',
      secondaryBtnLink: '/contact'
    },
    {
      number: '06',
      code: 'DEVOPS_PROD_DEPLOYMENT',
      icon: Rocket,
      title: 'Multi-Region Production Deployment & 24/7 Monitoring',
      badge: 'AWS / Vercel DevOps',
      timeline: 'Continuous SLA',
      description: 'We orchestrate automated CI/CD deployment to AWS/Vercel infrastructure with zero-downtime database migrations, continuous error telemetry, and 24/7 SLA maintenance support.',
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=800&q=80',
      color: 'from-emerald-500 to-teal-600',
      techTags: ['AWS Infrastructure', 'Vercel Pipelines', 'Zero Downtime', '24/7 SLA'],
      deliverables: ['Automated CI/CD AWS/Vercel Pipeline', 'Multi-Region Failover Setup', '24/7 SLA Uptime Telemetry'],
      primaryBtnText: 'Explore Cloud & DevOps Scaling',
      primaryBtnLink: '/cloud-devops',
      secondaryBtnText: 'Talk to Cloud Architects',
      secondaryBtnLink: '/contact'
    }
  ];

  // Auto-run slider interval (cycles stage every 4 seconds)
  useEffect(() => {
    if (isPaused) return;

    const timer = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % stages.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [isPaused, stages.length]);

  const handlePrev = () => {
    setActiveStage((prev) => (prev === 0 ? stages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveStage((prev) => (prev === stages.length - 1 ? 0 : prev + 1));
  };

  return (
    <section className="py-24 bg-slate-50 relative overflow-hidden border-t border-b border-gray-200 text-left">

      {/* Background Motion Glow Orbs */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-gradient-to-tr from-orange-200/40 via-amber-200/40 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" />
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-gradient-to-br from-cyan-200/40 via-blue-200/40 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" />

      <div className="container mx-auto px-6 relative z-10">

        {/* Section Header */}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center space-x-2 bg-orange-100/90 border border-orange-200 rounded-full px-4 py-1.5 mb-4 shadow-sm">
            <span className="text-orange-800 text-sm font-mono font-bold uppercase tracking-wider">
              6-STAGE WEB ENGINEERING METHODOLOGY
            </span>
          </div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4 leading-tight">
            Web Development <span className="bg-gradient-to-r from-orange-600 via-amber-500 to-cyan-600 bg-clip-text text-transparent">Execution Process</span>
          </h2>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed">
            Our structured 6-stage engineering process ensures sub-second page performance, robust architecture, and zero technical debt.
          </p>
        </div>

        {/* Horizontal Stage Stepper Buttons */}
        <div className="mb-10 relative hidden md:block">
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-orange-300 via-amber-300 to-cyan-300 -translate-y-1/2 z-0" />

          <div className="grid grid-cols-6 gap-3 relative z-10">
            {stages.map((stageItem, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStage(idx)}
                className={`flex flex-col items-center text-center p-3 rounded-2xl transition-all duration-300 ${activeStage === idx
                  ? 'bg-white border-2 border-orange-500 shadow-lg shadow-orange-500/10 scale-105'
                  : 'bg-white/80 border border-gray-200 hover:border-gray-300 opacity-80 hover:opacity-100 shadow-sm'
                  }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center font-mono font-bold text-sm mb-2 transition-all ${activeStage === idx
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600'
                  }`}>
                  {stageItem.number}
                </div>
                <span className="text-[11px] font-mono font-bold text-cyan-700 mb-0.5">{stageItem.code}</span>
                <span className="text-[11px] text-gray-700 line-clamp-1 font-semibold">{stageItem.badge}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Active Stage Interactive Console Card (Auto-running Slider) */}
        <div
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          className="bg-white border border-gray-200 rounded-3xl p-8 md:p-12 shadow-xl mb-12 relative overflow-hidden transition-all duration-300"
        >

          {/* Header Badge Strip */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <span className="text-3xl font-black font-mono text-orange-600">STAGE {stages[activeStage].number}</span>
              <span className="text-sm font-mono text-cyan-800 bg-cyan-100/80 border border-cyan-200 px-3 py-1 rounded-full font-bold">
                {stages[activeStage].code}
              </span>
            </div>
            <span className="text-sm font-mono text-emerald-800 bg-emerald-100/80 border border-emerald-200 px-3.5 py-1.5 rounded-full font-semibold">
              TIMELINE: {stages[activeStage].timeline}
            </span>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-center">

            {/* Left Column: Title, Description, Tech Badges & Buttons */}
            <div className="lg:col-span-7 space-y-5">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                {stages[activeStage].title}
              </h3>

              <p className="text-gray-600 text-sm md:text-base leading-relaxed">
                {stages[activeStage].description}
              </p>

              {/* Tech Stack Pills */}
              <div className="flex flex-wrap gap-2 pt-1">
                {stages[activeStage].techTags.map((tag, tIdx) => (
                  <span key={tIdx} className="bg-slate-100 border border-gray-200 text-slate-800 text-[11px] font-mono font-bold px-3 py-1 rounded-lg shadow-sm">
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Enterprise Solutions & Talk to Expert Style Buttons */}
              <div className="bg-slate-50 border border-gray-200 p-1.5 rounded-2xl inline-flex flex-wrap gap-2 shadow-sm pt-2 mt-4">
                <Link to={stages[activeStage].primaryBtnLink}>
                  <button className="px-5 py-2.5 rounded-xl text-sm md:text-sm font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-200" />
                    <span>{stages[activeStage].primaryBtnText}</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>

                <Link to={stages[activeStage].secondaryBtnLink}>
                  <button className="px-5 py-2.5 rounded-xl text-sm md:text-sm font-bold text-white bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-amber-200" />
                    <span>{stages[activeStage].secondaryBtnText}</span>
                  </button>
                </Link>
              </div>
            </div>

            {/* Right Column: Live Web Interface & Verified Deliverables */}
            <div className="lg:col-span-5 bg-slate-50 border border-gray-200 rounded-2xl p-6 shadow-sm">
              <div className="relative h-44 rounded-xl overflow-hidden mb-5 border border-gray-200">
                <img
                  src={stages[activeStage].image}
                  alt={stages[activeStage].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2 bg-slate-900/90 backdrop-blur-md px-2.5 py-0.5 rounded text-[11px] font-mono text-cyan-400 font-bold border border-slate-700">
                  WEB ENGINEERING SCREENSHOT
                </div>
              </div>

              <div className="text-sm font-mono text-gray-600 uppercase tracking-wider mb-3 font-bold flex items-center justify-between">
                <span>Verified Stage Milestones</span>
                <span className="text-emerald-700">3 Milestones Passed</span>
              </div>

              <div className="space-y-2">
                {stages[activeStage].deliverables.map((item, i) => (
                  <div key={i} className="flex items-center text-sm text-gray-800 bg-white p-2.5 rounded-lg border border-gray-200 shadow-sm">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mr-2 flex-shrink-0" />
                    <span className="truncate font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

          {/* Stepper Nav Controls with Looping Prev/Next */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handlePrev}
              className="text-sm font-mono font-bold px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-orange-500 border border-slate-900 hover:border-orange-500 shadow-sm transition-all cursor-pointer"
            >
              ← PREVIOUS STAGE
            </button>

            <div className="flex items-center space-x-2">
              <span className="text-xs font-mono font-bold text-gray-400 mr-2 hidden sm:inline">AUTO-PLAYING</span>
              {stages.map((_, i) => (
                <div
                  key={i}
                  onClick={() => setActiveStage(i)}
                  className={`h-2.5 rounded-full cursor-pointer transition-all duration-300 ${activeStage === i ? 'bg-gradient-to-r from-orange-500 to-amber-500 w-8' : 'bg-gray-300 hover:bg-gray-400 w-2.5'
                    }`}
                />
              ))}
            </div>

            <button
              onClick={handleNext}
              className="text-sm font-mono font-bold px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-orange-500 border border-slate-900 hover:border-orange-500 shadow-sm transition-all cursor-pointer"
            >
              NEXT STAGE →
            </button>
          </div>

        </div>

      </div>
    </section>
  );
};

export default WebDevelopmentProcess;
